import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import ModalSaveMode from "@/widgets/modalSaveMode/ui/ModalSaveMode/ModalSaveMode";
import styles from "@/widgets/modalSaveMode/ui/ModalSaveMode/ModalSaveMode.module.scss";
import { useCurrentOrganization } from "@/Hooks";
import { useGetSavedStorePointsAllQuery } from "@/features/auth/api/authAPI";

const SavedModeStoreView = () => {
  const { zoom, sortkey, userFilter } = useOutletContext();
  const [activeIndex, setActiveIndex] = useState(1);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [allResults, setAllResults] = useState<any[]>([]);
  const [sortDirectionSavedMode, setSortDirectionSavedMode] = useState("desc");
  const photoGroups = useRef<HTMLDivElement>(null);
  const currentOrganization = useCurrentOrganization();

  const handleItemClick = (index: number) => {
    setActiveIndex(index);
  };

  const {
    data: savedStoreScreens,
    refetch: refetchSavedScreens,
    isLoading,
    isFetching,
  } = useGetSavedStorePointsAllQuery(
    {
      organizationUuid: currentOrganization?.uuid || "",
      params: {
        sort: sortkey,
        user: userFilter,
        page: page,
        pageSize: PAGE_SIZE,
      },
    },
    {
      skip: !currentOrganization,
    },
  );

  useEffect(() => {
    if (savedStoreScreens?.results) {
      if (page === 1) {
        setAllResults(savedStoreScreens.results);
      } else {
        // Append new results, avoiding duplicates
        const newItems = savedStoreScreens.results.filter(
          (newItem) =>
            !allResults.some((existingItem) => existingItem.id === newItem.id),
        );
        if (newItems.length > 0) {
          setAllResults((prev) => [...prev, ...newItems]);
        }
      }
    }
  }, [savedStoreScreens?.results, page]);

  const handleLocalDelete = useCallback(
    (deletedItemIds: number[]) => {
      setAllResults((prevResults) =>
        prevResults.filter((item) => !deletedItemIds.includes(item.id)),
      );

      // After local deletion, refetch the *current* page.
      refetchSavedScreens();
    },
    [refetchSavedScreens],
  );

  const handleItemsAdded = useCallback((newItems: any[]) => {
    setAllResults((prevResults) => {
      // Check for duplicates and add only new items
      const existingIds = new Set(prevResults.map((item) => item.id));
      const uniqueNewItems = newItems.filter(
        (item) => !existingIds.has(item.id),
      );

      if (uniqueNewItems.length > 0) {
        // Add new items to the beginning of the array (most recent first)
        return [...uniqueNewItems, ...prevResults];
      }
      return prevResults;
    });
  }, []);

  const combinedData = {
    ...(savedStoreScreens || {}), // Spread current API response
    results: allResults,
    count: allResults.length, // Client-side count
    // Logic for 'next' based on local state and last API fetch
    next:
      savedStoreScreens?.next &&
      allResults.length > 0 &&
      allResults.length === page * PAGE_SIZE
        ? savedStoreScreens.next
        : null,
  };

  const applyZoom = (zoom: number) => {
    const photoCardListElement = photoGroups.current;
    if (photoCardListElement) {
      photoCardListElement.style.setProperty("--zoom-level", zoom.toString());
    }
  };

  useEffect(() => {
    applyZoom(zoom);
  }, [zoom]);

  return (
    <div className={styles.mainContent}>
      <div ref={photoGroups} className={styles.photosContainer}>
        <ModalSaveMode
          dataSavedPoint={combinedData}
          activeIndex={activeIndex}
          handleItemClick={handleItemClick}
          apiDataSharedViews={undefined}
          apiDataSavedStoreScreens={undefined}
          onItemsDeleted={handleLocalDelete}
          onItemsAdded={handleItemsAdded}
          apiDataSharedItem={undefined}
          sharedGroupsQuery={null}
          refetchSavedPoints={refetchSavedScreens}
          apiDataSharedItemSavedPoints={undefined}
          apiDataSharedItemStoreScreens={undefined}
          sortDirection={sortDirectionSavedMode}
          setSortDirection={setSortDirectionSavedMode}
          viewType="store"
          page={page}
          setPage={setPage}
          isLoading={isLoading || isFetching}
          isFetching={isFetching}
          pageSize={PAGE_SIZE}
        />
      </div>
    </div>
  );
};

export { SavedModeStoreView };
